import * as Sentry from "@sentry/react";
import packageJson from "../package.json";

interface SentryConfig {
  dsn: string;
  environment: string;
  sendDefaultPii: boolean;
  enableInExpensiveErrorBoundary: boolean;
  tracesSampleRate: number;
  beforeSend?: (
    event: Sentry.ErrorEvent,
    hint: Sentry.EventHint,
  ) => Sentry.ErrorEvent | PromiseLike<Sentry.ErrorEvent | null> | null;
}

const getSentryConfig = (): SentryConfig => {
  const mode = import.meta.env.MODE || "development";
  const isProduction = mode === "production";
  const appVersion = packageJson.version;

  // For production, use the full hostname (including subdomains) as environment
  // For non-production, fall back to the mode
  const environment =
    isProduction && typeof window !== "undefined" ? window.location.hostname : mode;

  return {
    dsn: "https://7c42d57a265ea35372bd16d8726342df@o1012318.ingest.us.sentry.io/4509445690687488",
    environment,
    sendDefaultPii: true,
    enableInExpensiveErrorBoundary: true,
    tracesSampleRate: isProduction ? 0.1 : 1.0, // Lower sampling in production
    beforeSend: (event) => {
      // Filter out development-related errors
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.value?.includes("development") || error?.value?.includes("dev-server")) {
          return null;
        }
      }

      // Add additional context
      if (event.contexts) {
        event.contexts.widget = {
          version: appVersion,
          mode,
          environment,
        };
      }

      return event;
    },
  };
};

export const initializeSentry = (): void => {
  const mode = import.meta.env.MODE || "development";
  const isDebugMode = import.meta.env.VITE_DEBUG === "true";
  const isProduction = mode === "production";

  // Skip Sentry initialization during Playwright tests
  const isPlaywrightTest = import.meta.env.VITE_PLAYWRIGHT_TEST === "true";

  if (isPlaywrightTest) {
    return;
  }

  // Skip Sentry initialization in development unless debug mode is enabled
  if (mode === "development" && !isDebugMode) {
    return;
  }

  const config = getSentryConfig();
  const appVersion = packageJson.version;

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    sendDefaultPii: config.sendDefaultPii,
    tracesSampleRate: config.tracesSampleRate,
    beforeSend: config.beforeSend,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Session Replay sampling rates
    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Release tracking
    release: appVersion,

    // Error filtering
    ignoreErrors: [],

    // URL filtering
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  // Set additional context
  Sentry.setContext("app", {
    name: "Blockdaemon Widget",
    version: appVersion,
  });
};
